import './need-access-button.component.scss';

import type { FunctionComponent } from 'preact';
import { useState } from 'preact/hooks';

import { useAuth } from '../../../auth/hooks/use-auth.hook';
import { SpinnerComponent } from '../../../common/components/spinner/spinner.component';
import { IconLock } from '../../../common/icons/icon-lock';
import { IconPlay } from '../../../common/icons/icon-play';
import { IconPlayerPlay } from '../../../common/icons/icon-player-play';
import { selectNeedAccessLoading } from '../../../content/store/content.selectors';
import { t, useTranslation } from '../../../i18n';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import type { NeedAccessContentType, NeedAccessReq } from '../../need-access.interface';
import { NeedAccessService } from '../../need-access.service';

interface NeedAccessButtonProps {
  message: string | undefined;
  showLock: boolean;
  isPlayBtn?: boolean;
  /** Render the standard player glyph (IconPlayerPlay) instead of the hero video glyph. */
  playerIcon?: boolean;
  contentType: NeedAccessContentType;
  contentId: string;
  modalMaxWidth?: number;
  modalBorderRadius?: string;
  modalBorderWidth?: string;
  modalBorderColor?: string;
  backgroundColor?: string;
  textColor?: string;
}

export const NeedAccessButton: FunctionComponent<NeedAccessButtonProps> = ({
  message = t('access.joinToUnlock'),
  showLock = true,
  isPlayBtn,
  playerIcon,
  contentType,
  contentId,
  modalMaxWidth,
  modalBorderRadius,
  modalBorderWidth,
  modalBorderColor,
  backgroundColor,
  textColor,
}: NeedAccessButtonProps) => {
  const { t } = useTranslation();
  const loading: boolean = useAppSelector(selectNeedAccessLoading);
  const [pending, setPending] = useState(false);
  const needAccessService = new NeedAccessService();
  const dispatch = useAppDispatch();
  const { openAuthModal } = useAuth();

  // Only set when configured so the button keeps inheriting theme styles otherwise
  const buttonStyle = {
    ...(backgroundColor ? { backgroundColor } : {}),
    ...(textColor ? { color: textColor } : {}),
  };

  const onNeedAccessClicked = async () => {
    if (loading || pending) return;

    const req: NeedAccessReq = {
      contentId: contentId,
      contentType: contentType,
    };
    setPending(true);
    try {
      await needAccessService.processNeedAccessRequest({
        request: req,
        message,
        modalMaxWidth,
        modalBorderRadius,
        modalBorderWidth,
        modalBorderColor,
        dispatch,
        openAuthModal,
      });
    } finally {
      // Runs whether the modal opened, the request failed, or the service
      // declined the call because another request was already in flight.
      setPending(false);
    }
  };

  return (
    <>
      {!isPlayBtn && (
        <button
          class="single-need-access-btn btn button"
          style={buttonStyle}
          disabled={loading || pending}
          onClick={onNeedAccessClicked}
        >
          {(loading || pending) && <span>{t('common.loading')}</span>}
          {!(loading || pending) && showLock && <IconLock />}
          {!(loading || pending) && <span>{message}</span>}
        </button>
      )}

      {isPlayBtn && (
        <button
          class="single-need-access-btn button play-btn"
          style={buttonStyle}
          disabled={loading || pending}
          aria-label={message}
          onClick={onNeedAccessClicked}
        >
          {(loading || pending) && <SpinnerComponent />}
          {!(loading || pending) &&
            (playerIcon ? <IconPlayerPlay fill="#fff" /> : <IconPlay fill="#fff" />)}
        </button>
      )}
    </>
  );
};
